import type {
  PaginatedResponse,
  PaginationCursorMeta,
  PaginationMeta,
} from "@design-system/pagination/schemas";
import z from "zod/v4";

interface CursorList<TRow> {
  data: TRow[];
  pagination: PaginationCursorMeta;
}

interface CountedList<TRow> {
  data: TRow[];
  totalCount: number;
}

const OffsetListSchema = z.object({
  data: z.array(z.unknown()),
  totalElements: z.number().int(),
  offset: z.number().int(),
  limit: z.number().int(),
});

function toMeta(
  totalCount: number,
  page: number,
  pageSize: number
): PaginationMeta {
  return {
    page,
    pageSize,
    totalCount,
    totalPages: pageSize > 0 ? Math.ceil(totalCount / pageSize) : 0,
  };
}

function parseRowsTolerantly<TSchema extends z.ZodType>(
  schema: TSchema,
  rows: unknown[],
  entity: string,
  idKey = "id"
): z.infer<TSchema>[] {
  return rows.flatMap((row) => {
    const result = schema.safeParse(row);
    if (result.success) return [result.data];

    const id =
      row && typeof row === "object"
        ? (row as Record<string, unknown>)[idKey]
        : undefined;
    console.warn(`Skipping unparseable ${entity} row`, {
      id,
      issues: result.error.issues.map((issue) => ({
        path: issue.path,
      })),
    });
    return [];
  });
}

function createOffsetListSchema<TSchema extends z.ZodType>(
  rowSchema: TSchema,
  entity: string
) {
  return OffsetListSchema.transform(
    (envelope): PaginatedResponse<z.infer<TSchema>> => ({
      data: parseRowsTolerantly(rowSchema, envelope.data, entity),
      meta: toMeta(
        envelope.totalElements,
        envelope.limit > 0
          ? Math.floor(envelope.offset / envelope.limit) + 1
          : 1,
        envelope.limit
      ),
    })
  );
}

function createPageListSchema<TSchema extends z.ZodType>(
  rowSchema: TSchema,
  entity: string
) {
  return z
    .object({
      data: z.array(z.unknown()),
      page: z.number().int(),
      pageSize: z.number().int(),
      total: z.number().int(),
    })
    .transform((envelope): PaginatedResponse<z.infer<TSchema>> => ({
      data: parseRowsTolerantly(rowSchema, envelope.data, entity),
      meta: toMeta(envelope.total, envelope.page + 1, envelope.pageSize),
    }));
}

function createNestedPageListSchema<TSchema extends z.ZodType>(
  rowSchema: TSchema,
  entity: string
) {
  return z
    .object({
      data: z.array(z.unknown()),
      pagination: z.object({
        currentPage: z.number().int(),
        itemCount: z.number().int(),
        itemsPerPage: z.number().int(),
        totalItems: z.number().int().optional(),
        totalPages: z.number().int().optional(),
      }),
    })
    .transform((envelope): PaginatedResponse<z.infer<TSchema>> => {
      const { pagination } = envelope;
      const totalCount =
        pagination.totalItems ??
        (pagination.currentPage - 1) * pagination.itemsPerPage +
          pagination.itemCount;

      const pageIsFull =
        pagination.itemsPerPage > 0 &&
        pagination.itemCount === pagination.itemsPerPage;
      const mayHaveMore = pagination.totalItems === undefined && pageIsFull;

      return {
        data: parseRowsTolerantly(rowSchema, envelope.data, entity),
        meta: {
          page: pagination.currentPage,
          pageSize: pagination.itemsPerPage,
          totalCount,
          totalPages:
            pagination.totalPages ??
            (pagination.itemsPerPage > 0
              ? Math.ceil(totalCount / pagination.itemsPerPage) +
                (mayHaveMore ? 1 : 0)
              : 0),
        },
      };
    });
}

const NEXT_CURSOR_PREFIX = "next:";
const PREVIOUS_CURSOR_PREFIX = "previous:";

interface CursorWireParams {
  nextPageCursor?: string;
  previousPageCursor?: string;
}

function toCursorWireParams(cursor?: string): CursorWireParams {
  if (cursor?.startsWith(NEXT_CURSOR_PREFIX)) {
    return { nextPageCursor: cursor.slice(NEXT_CURSOR_PREFIX.length) };
  }
  if (cursor?.startsWith(PREVIOUS_CURSOR_PREFIX)) {
    return { previousPageCursor: cursor.slice(PREVIOUS_CURSOR_PREFIX.length) };
  }
  return {};
}

function createCursorListSchema<TSchema extends z.ZodType>(
  rowSchema: TSchema,
  entity: string,
  idKey?: string
) {
  return z
    .object({
      data: z.array(z.unknown()),
      nextPageCursor: z.string().min(1).nullish(),
      previousPageCursor: z.string().min(1).nullish(),
    })
    .transform((envelope): CursorList<z.infer<TSchema>> => ({
      data: parseRowsTolerantly(rowSchema, envelope.data, entity, idKey),
      pagination: {
        hasNext: Boolean(envelope.nextPageCursor),
        hasPrevious: Boolean(envelope.previousPageCursor),
        nextCursor: envelope.nextPageCursor
          ? `${NEXT_CURSOR_PREFIX}${envelope.nextPageCursor}`
          : undefined,
        prevCursor: envelope.previousPageCursor
          ? `${PREVIOUS_CURSOR_PREFIX}${envelope.previousPageCursor}`
          : undefined,
      },
    }));
}

function createCountedListSchema<TSchema extends z.ZodType>(
  rowSchema: TSchema,
  entity: string
) {
  return OffsetListSchema.pick({
    data: true,
    totalElements: true,
  }).transform((envelope): CountedList<z.infer<TSchema>> => ({
    data: parseRowsTolerantly(rowSchema, envelope.data, entity),
    totalCount: envelope.totalElements,
  }));
}

function withPagePosition<TRow>(
  counted: CountedList<TRow>,
  pageIndex: number,
  pageSize: number
): PaginatedResponse<TRow> {
  return {
    data: counted.data,
    meta: toMeta(counted.totalCount, pageIndex, pageSize),
  };
}

export {
  createCountedListSchema,
  createCursorListSchema,
  createNestedPageListSchema,
  createOffsetListSchema,
  createPageListSchema,
  parseRowsTolerantly,
  toCursorWireParams,
  toMeta,
  withPagePosition,
};
export type { CountedList, CursorList, CursorWireParams };
